import { useState } from 'react';

type Props = {
  amountLabel: string;
  summaryLine: string;
  onSimulate: () => void;
  done: boolean;
};

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

export default function SimulatedCheckout({ amountLabel, summaryLine, onSimulate, done }: Props) {
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '' });
  const [used, setUsed] = useState(false);

  const useSample = () => {
    setCard({ number: '4242 4242 4242 4242', expiry: '12 / 28', cvc: '123' });
    setUsed(true);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-lg"><i className="ri-bank-card-line"></i></span>
          <h3 className="font-heading font-bold text-xl text-foreground-950">Simulated checkout</h3>
        </div>
        <span className="bg-accent-100 text-accent-900 border border-accent-300 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm whitespace-nowrap">
          No real payment
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-background-100 border border-background-200 px-4 py-3">
        <span className="text-sm text-foreground-700">{summaryLine}</span>
        <span className="font-heading font-extrabold text-2xl text-foreground-950">{amountLabel}</span>
      </div>

      {done ? (
        <p className="mt-4 bg-primary-100 border border-primary-200 text-primary-800 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-checkbox-circle-line"></i></span>
          <span><strong className="font-semibold">Simulated payment confirmed.</strong> No card was charged and nothing was stored.</span>
        </p>
      ) : (
        <>
          <p className="mt-4 text-xs text-foreground-500 flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
            This is a visual demonstration only. Do not enter real card details &mdash; use the sample card below, and nothing is ever transmitted.
          </p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label htmlFor="rc-number" className={labelClass}>Card number</label>
              <input id="rc-number" value={card.number} onChange={(e) => setCard((p) => ({ ...p, number: e.target.value }))} placeholder="Card number" inputMode="numeric" autoComplete="off" className={`${fieldClass} font-mono`} />
            </div>
            <div>
              <label htmlFor="rc-exp" className={labelClass}>Expiry</label>
              <input id="rc-exp" value={card.expiry} onChange={(e) => setCard((p) => ({ ...p, expiry: e.target.value }))} placeholder="MM / YY" autoComplete="off" className={`${fieldClass} font-mono`} />
            </div>
            <div>
              <label htmlFor="rc-cvc" className={labelClass}>CVC</label>
              <input id="rc-cvc" value={card.cvc} onChange={(e) => setCard((p) => ({ ...p, cvc: e.target.value }))} placeholder="123" autoComplete="off" className={`${fieldClass} font-mono`} />
            </div>
            <div className="flex items-end">
              <button type="button" onClick={useSample} className="w-full inline-flex items-center justify-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-magic-line"></i></span>
                Use sample card
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onSimulate}
            disabled={!used}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>
            Simulate payment
          </button>
          {!used && <p className="mt-2 text-xs text-foreground-500">Use the sample card to enable the simulated payment.</p>}
        </>
      )}
    </div>
  );
}